/**
 * ============================================================================
 * CopyLinkButton — زرّ مشاركة بسيط للزوّار العاديّين
 * ============================================================================
 * ينسخ الرابط للحافظة ويعرض toast. لا navigator.share، لا ملفّات، لا تتبّع.
 * المسار الوحيد لمشاركة السفير بكوده هو /account/media (المكتبة).
 * يُستخدم في صفحة المنتج · البانرات · المجموعات · أيّ مكان عامّ.
 * ============================================================================
 */

import * as React from "react";
import { Share2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { useLocale } from "@/contexts/LocaleContext";
import { cn } from "@/lib/utils";

interface Props {
  url: string;
  className?: string;
  ariaLabel?: string;
  /** أيقونة فقط أم أيقونة + نصّ */
  variant?: "icon" | "button";
}

export function CopyLinkButton({ url, className, ariaLabel, variant = "icon" }: Props) {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const label = isAr ? "انسخ الرابط" : "Copy link";

  const onClick = React.useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      try {
        if (typeof navigator?.clipboard?.writeText === "function") {
          await navigator.clipboard.writeText(url);
        } else {
          const ta = document.createElement("textarea");
          ta.value = url;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        toast.success(isAr ? "✅ تمّ نسخ الرابط" : "✅ Link copied", {
          description: isAr
            ? "الصقه في تطبيقك المفضّل لمشاركة المنتج."
            : "Paste it in your favourite app to share.",
          duration: 4000,
        });
      } catch {
        toast.error(isAr ? "تعذّر النسخ — انسخ يدويّاً" : "Copy failed — try manually");
      }
    },
    [url, isAr],
  );

  if (variant === "button") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? label}
        title={ariaLabel ?? label}
        className={cn(
          "inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm text-foreground transition-colors hover:bg-muted min-h-[40px]",
          className,
        )}
      >
        <Share2 className="h-4 w-4" strokeWidth={1.5} />
        <span>{label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      title={ariaLabel ?? label}
      className={cn(
        "inline-flex items-center justify-center p-2 text-foreground/80 transition-colors hover:text-primary min-h-[40px] min-w-[40px]",
        className,
      )}
    >
      <Share2 className="h-5 w-5" strokeWidth={1.5} />
    </button>
  );
}
